import { useContext } from 'react';
import { useQuery, useMutation, QueryClient } from '@tanstack/react-query';
import { ApiContext } from '@shared/contexts';
import {
  TSubmission,
  TCreateSubmissionDto,
  TSparseSubmission,
  TGetSubmissionReturn,
  TSelectItemsReturn,
  TSelectedSubmissionItemDto,
  TUpdateSubmissionItemDto,
  TSubmissionItem,
  TTransitionSubmissionDto,
  TUpdateSubmissionDetailsDto,
  TSubmissionItemInfo,
} from './types/submissions.types';
import { usePollBulkExport, TBulkExport, TResolvedItemsMap, TVersionPreference } from '@modules/items';
import { TUseDocumentResult } from '../documents';
import { TUseFileResult } from '../files';
import { TUseReplicaResult } from '../replicas/types';

// --- HELPER:

const updateSubmissionQueryData = (
  queryClient: QueryClient,
  submissionId: string,
  updateOptions: {
    submission?: TSubmission;
    partialSubmission?: Partial<TSubmission>;
    resolvedItems?: TResolvedItemsMap;
  }
) => {
  queryClient.setQueryData(
    [`submission-${submissionId}`],
    (oldData: TGetSubmissionReturn | undefined): TGetSubmissionReturn | undefined => {
      return oldData
        ? {
            ...oldData,
            // no submission option
            submission: updateOptions.submission
              ? updateOptions.submission // submission option
              : updateOptions.partialSubmission
                ? { ...oldData.submission, ...updateOptions.partialSubmission } // partialSubmission option
                : oldData.submission,
            resolvedItems: updateOptions.resolvedItems || oldData.resolvedItems, // resolvedItems option
          }
        : oldData;
    }
  );
};

const updateSubmissionItemQueryData = (
  queryClient: QueryClient,
  submissionId: string,
  submissionItem: TSubmissionItem
) => {
  queryClient.setQueryData(
    [`submission-${submissionId}`],
    (oldData: TGetSubmissionReturn | undefined): TGetSubmissionReturn | undefined => {
      return oldData
        ? {
            ...oldData,
            submission: {
              ...oldData.submission,
              selectedItems: oldData.submission.selectedItems.map((submissionItemIter) =>
                submissionItemIter.item === submissionItem.item ? submissionItem : submissionItemIter
              ),
            },
          }
        : oldData;
    }
  );
  const parsesubmissionItemInfos = (submissionItemInfos: TSubmissionItemInfo[]) =>
    submissionItemInfos.map((submissionItemInfo) => {
      if (submissionItemInfo.submissionItem.item === submissionItem.item)
        return { ...submissionItemInfo, submissionItem };
      return submissionItemInfo;
    });
  queryClient.setQueryData(
    [`document-${submissionItem.item}`],
    (oldData: TUseDocumentResult | undefined): TUseDocumentResult | undefined =>
      oldData && {
        ...oldData,
        doc: { ...oldData.doc, submissionItemInfos: parsesubmissionItemInfos(oldData.doc.submissionItemInfos) },
      }
  );
  queryClient.setQueryData(
    [`file-${submissionItem.item}`],
    (oldData: TUseFileResult | undefined): TUseFileResult | undefined =>
      oldData && {
        ...oldData,
        file: { ...oldData.file, submissionItemInfos: parsesubmissionItemInfos(oldData.file.submissionItemInfos) },
      }
  );
  // Replicas: submissionItemInfos are nested inside masterData.doc / masterData.file
  queryClient.setQueryData(
    [`replica-${submissionItem.item}`],
    (oldData: TUseReplicaResult | undefined): TUseReplicaResult | undefined => {
      if (!oldData) return oldData;
      const docResult = oldData.masterData as TUseDocumentResult;
      if (docResult.doc) {
        return {
          ...oldData,
          masterData: {
            ...docResult,
            doc: { ...docResult.doc, submissionItemInfos: parsesubmissionItemInfos(docResult.doc.submissionItemInfos) },
          },
        };
      }
      const fileResult = oldData.masterData as TUseFileResult;
      return {
        ...oldData,
        masterData: {
          ...fileResult,
          file: {
            ...fileResult.file,
            submissionItemInfos: parsesubmissionItemInfos(fileResult.file.submissionItemInfos),
          },
        },
      };
    }
  );
};

// --- GET:

/**
 * Hook: useSubmissions
 * ---------------------------------
 * Retrieves all submissions for a given product.
 *
 * Endpoint:
 *   GET /submissions?product=:productId
 *
 * @param productId - The product ID to fetch submissions for.
 * @returns An object with the submissions data, loading state, and error details.
 */
export const useSubmissions = (productId: string) => {
  const { apiClient, useHandleQueryError, retry } = useContext(ApiContext);

  const getSubmissions = async (): Promise<TSparseSubmission[]> => {
    const response = await apiClient.get(`/submissions?product=${productId}`);
    return response.data;
  };

  const { data, isError, error, isLoading } = useQuery({
    queryKey: [`submissions-${productId}`],
    queryFn: getSubmissions,
    retry,
  });

  useHandleQueryError(
    isError,
    error,
    `Failed to load submissions for product ${productId}!`,
    `Loading submissions failed!`
  );

  return { data, isError, error, isLoading };
};

/**
 * Hook: useSubmission
 * ---------------------------------
 * Retrieves detailed information for a single submission.
 *
 * Endpoint:
 *   GET /submissions/:id
 *
 * @param submissionId - The ID of the submission to fetch.
 * @returns An object with the submission data, loading state, and error details.
 */
export const useSubmission = (submissionId: string) => {
  const { apiClient, useHandleQueryError, retry } = useContext(ApiContext);

  const getSubmission = async (): Promise<TGetSubmissionReturn> => {
    const response = await apiClient.get(`/submissions/${submissionId}`);
    return response.data;
  };

  const { data, isError, error, isLoading } = useQuery({
    queryKey: [`submission-${submissionId}`],
    queryFn: getSubmission,
    retry,
  });

  useHandleQueryError(isError, error, `Failed to load submission ${submissionId}!`, `Loading submission failed!`);

  return { data, isError, error, isLoading };
};

// --- POST:

/**
 * Hook: useCreateSubmission
 * ---------------------------------
 * Creates a new submission for a given product.
 *
 * Endpoint:
 *   POST /submissions
 *
 * Usage:
 *   const createSubmissionMutation = useCreateSubmission();
 *   createSubmissionMutation.mutate(submissionData);
 *
 * @returns A mutation object for handling the create submission operation.
 */
export const useCreateSubmission = () => {
  const { apiClient, show, handleApiError } = useContext(ApiContext);

  const mutationFn = async (submissionData: TCreateSubmissionDto): Promise<string> => {
    // Expects submissionData to follow the TCreateSubmissionDto structure.
    const response = await apiClient.post('/submissions', submissionData);
    return response.data; // returns the created submission's ID
  };

  return useMutation({
    mutationFn,
    onSuccess: () => {
      show('success', 'Created submission.');
    },
    onError: handleApiError('Creating submission failed!'),
  });
};

/**
 * Hook to start a bulk export job asynchronously.
 */
export const useBulkExportSubmission = () => {
  const { apiClient, queryClient, handleApiError } = useContext(ApiContext);

  const { isPolling, setJobId } = usePollBulkExport();

  const mutationFn = async ({
    submissionId,
    ...exportData
  }: {
    submissionId: string;
    itemIds?: string[];
    mode: TVersionPreference;
  }): Promise<TBulkExport> => {
    if (isPolling) throw new Error('Export already in progress');
    // starts the background job
    const { data } = await apiClient.post(`/submissions/${submissionId}/export`, exportData);
    return data;
  };

  const mutation = useMutation({
    mutationFn,
    onSuccess: (data) => {
      setJobId(data._id);
      queryClient.setQueryData([`bulk-export-${data._id}`], () => data);
    },
    onError: handleApiError('Export failed!', true),
  });

  return { ...mutation, isPending: mutation.isPending || isPolling };
};

// --- PUT:

/**
 * Hook: useSelectSubmissionItems
 * ---------------------------------
 * Updates the submission's list of "selectedItems".
 * Accepts typed `{ itemId, itemType }` objects - folders are resolved into
 * concrete DOCUMENT/FILE items on the backend.
 *
 * Endpoint:
 *   PUT /submissions/:id/items
 *
 * @param submissionId The ID of the submission
 *
 * Usage:
 *   const selectItemsMutation = useSelectSubmissionItems(submissionId);
 *   selectItemsMutation.mutate([{ itemId: '...', itemType: 'DOCUMENT' }]);
 */
export const useSelectSubmissionItems = (submissionId: string) => {
  const { apiClient, show, queryClient, handleApiError } = useContext(ApiContext);

  // The mutation function
  const mutationFn = async (selectedItems: TSelectedSubmissionItemDto[]): Promise<TSelectItemsReturn> => {
    // PUT /submissions/:id/items with { selectedItems: { itemId, itemType }[] }
    const response = await apiClient.put(`/submissions/${submissionId}/items`, {
      selectedItems,
    });
    return response.data;
  };

  return useMutation({
    mutationFn,
    onSuccess: (data) => {
      // data is TSelectItemsReturn
      show('success', 'Updated items of submission.');

      // Update the query data for the submission
      updateSubmissionQueryData(queryClient, submissionId, {
        partialSubmission: { selectedItems: data.selectedItems },
        resolvedItems: data.resolvedItems,
      });
    },
    onError: handleApiError('Updating items of submission failed!'),
  });
};

export const useUpdateSubmissionItem = () => {
  const { apiClient, queryClient, show, handleApiError } = useContext(ApiContext);

  // The mutation function
  const mutationFn = async (variables: {
    submissionId: string;
    submissionItemId: string;
    updateData: TUpdateSubmissionItemDto;
  }): Promise<TSubmissionItem> => {
    // PUT /submissions/:id/items/:itemId
    const response = await apiClient.put(
      `/submissions/${variables.submissionId}/items/${variables.submissionItemId}`,
      variables.updateData
    );
    return response.data;
  };

  return useMutation({
    mutationFn,
    onSuccess: (data, variables) => {
      // data is TSelectItemsReturn
      show('success', 'Updated submission item.');
      updateSubmissionItemQueryData(queryClient, variables.submissionId, data);
    },
    onError: handleApiError('Updating submission item failed!'),
  });
};

export const useUpdateSubmissionDetails = () => {
  const { apiClient, queryClient, show, handleApiError } = useContext(ApiContext);

  const mutationFn = async (variables: {
    submissionId: string;
    updateData: TUpdateSubmissionDetailsDto;
  }): Promise<TSubmission> => {
    // PUT /submissions/:id/details
    const response = await apiClient.put(`/submissions/${variables.submissionId}/details`, variables.updateData);
    return response.data;
  };

  return useMutation({
    mutationFn,
    onSuccess: (data, variables) => {
      show('success', 'Updated submission details.');
      updateSubmissionQueryData(queryClient, variables.submissionId, {
        submission: data,
      });
    },
    onError: handleApiError('Updating submission details failed!'),
  });
};

export const useTransitionSubmission = () => {
  const { apiClient, queryClient, show, handleApiError } = useContext(ApiContext);

  const mutationFn = async (variables: {
    submissionId: string;
    updateData: TTransitionSubmissionDto;
  }): Promise<TSubmission> => {
    // PUT /submissions/:id/transition
    const response = await apiClient.put(`/submissions/${variables.submissionId}/transition`, variables.updateData);
    return response.data;
  };

  return useMutation({
    mutationFn,
    onSuccess: (data, variables) => {
      show('success', 'Updated submission status.');
      updateSubmissionQueryData(queryClient, variables.submissionId, {
        submission: data,
      });
    },
    onError: handleApiError('Transitioning submission failed!'),
  });
};

// --- DELETE:

export const useDeleteSubmission = () => {
  const { apiClient, queryClient, show, handleApiError } = useContext(ApiContext);

  const mutationFn = async (variables: { submissionId: string }): Promise<void> => {
    // DELETE /submissions/:id
    await apiClient.delete(`/submissions/${variables.submissionId}`);
  };

  return useMutation({
    mutationFn,
    onSuccess: (_, variables) => {
      show('success', 'Deleted submission.');
      // potentially dangerous: queryClient.removeQueries({ queryKey: [`submission-${variables.submissionId}`] });
    },
    onError: handleApiError('Deleting submission failed!'),
  });
};
